import { call, put, takeEvery, takeLatest, select, all, fork } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  bomApi,
  SearchBOMsRequest, SearchBOMsResponse,
  SearchComponentsRequest, SearchComponentsResponse,
  SearchProductsRequest, SearchProductsResponse,
  CreateBOMRequest, CreateComponentRequest, CreateProductRequest,
  BOM, Component, Product, BOMStructure
} from '../../services/bomApi';
import { WorkOrder } from '../../services/workOrderApi';
import {
  // BOM Actions
  fetchBOMsStart, fetchBOMsSuccess, fetchBOMsFailure,
  fetchBOMByIdStart, fetchBOMByIdSuccess, fetchBOMByIdFailure,
  createBOMStart, createBOMSuccess, createBOMFailure,
  updateBOMStart, updateBOMSuccess, updateBOMFailure,
  deleteBOMStart, deleteBOMSuccess, deleteBOMFailure,
  
  // Component Actions
  fetchComponentsStart, fetchComponentsSuccess, fetchComponentsFailure,
  fetchComponentByIdStart, fetchComponentByIdSuccess, fetchComponentByIdFailure,
  createComponentStart, createComponentSuccess, createComponentFailure,
  updateComponentStart, updateComponentSuccess, updateComponentFailure,
  deleteComponentStart, deleteComponentSuccess, deleteComponentFailure,
  
  // Product Actions
  fetchProductsStart, fetchProductsSuccess, fetchProductsFailure,
  fetchProductByIdStart, fetchProductByIdSuccess, fetchProductByIdFailure,
  createProductStart, createProductSuccess, createProductFailure,
  updateProductStart, updateProductSuccess, updateProductFailure,
  deleteProductStart, deleteProductSuccess, deleteProductFailure,
  
  // Work Order Actions
  fetchWorkOrdersStart, fetchWorkOrdersSuccess, fetchWorkOrdersFailure,
  linkProductToWorkOrderStart, linkProductToWorkOrderSuccess, linkProductToWorkOrderFailure,
  unlinkProductFromWorkOrderStart, unlinkProductFromWorkOrderSuccess, unlinkProductFromWorkOrderFailure,
  
  // BOM Structure Actions
  fetchBOMStructureStart, fetchBOMStructureSuccess, fetchBOMStructureFailure,
  
  // Dropdown Actions
  fetchDropdownsStart, fetchDropdownsSuccess, fetchDropdownsFailure,
  
  // Validation Actions
  setBOMValidationErrors, setComponentValidationErrors, setProductValidationErrors,
  
  // Export Actions
  exportBOMStart, exportBOMSuccess, exportBOMFailure,
} from '../reducers/bomSlice';
import { RootState } from '../index';

// BOM Sagas
function* fetchBOMsApi(action: PayloadAction<SearchBOMsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { bomPagination, bomSearchTerm, bomActiveFilter } = state.bom;
    
    const params: SearchBOMsRequest = action.payload || {
      pageNum: bomPagination.currentPage,
      pageSize: bomPagination.pageSize,
      filter: {
        ...(bomSearchTerm && { Name: bomSearchTerm }),
        ...(bomActiveFilter !== null && { Active: bomActiveFilter }),
      },
    };
    
    const response: SearchBOMsResponse = yield call(bomApi.getAllBOMs, params);
    yield put(fetchBOMsSuccess(response));
  } catch (error: any) {
    yield put(fetchBOMsFailure(error.message || 'Failed to fetch BOMs'));
  }
}

function* fetchBOMByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const bom: BOM = yield call(bomApi.getBOMById, action.payload);
    yield put(fetchBOMByIdSuccess(bom));
  } catch (error: any) {
    yield put(fetchBOMByIdFailure(error.message || 'Failed to fetch BOM'));
  }
}

function* createBOMApi(action: PayloadAction<CreateBOMRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = bomApi.validateBOM(action.payload);
    if (validationErrors.length > 0) {
      yield put(setBOMValidationErrors(validationErrors));
      yield put(createBOMFailure('Validation failed'));
      return;
    }
    
    const bom: BOM = yield call(bomApi.createBOM, action.payload);
    yield put(createBOMSuccess(bom));
    
    // Refresh the list
    yield put(fetchBOMsStart());
  } catch (error: any) {
    yield put(createBOMFailure(error.message || 'Failed to create BOM'));
  }
}

function* updateBOMApi(action: PayloadAction<Partial<BOM>>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = bomApi.validateBOM(action.payload);
    if (validationErrors.length > 0) {
      yield put(setBOMValidationErrors(validationErrors));
      yield put(updateBOMFailure('Validation failed'));
      return;
    }
    
    const bom: BOM = yield call(bomApi.updateBOM, action.payload);
    yield put(updateBOMSuccess(bom));
    
    // Refresh the list
    yield put(fetchBOMsStart());
  } catch (error: any) {
    yield put(updateBOMFailure(error.message || 'Failed to update BOM'));
  }
}

function* deleteBOMApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(bomApi.deleteBOM, action.payload);
    yield put(deleteBOMSuccess(action.payload));
    
    // Refresh the list
    yield put(fetchBOMsStart());
  } catch (error: any) {
    yield put(deleteBOMFailure(error.message || 'Failed to delete BOM'));
  }
}

// Component Sagas
function* fetchComponentsApi(action: PayloadAction<SearchComponentsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { componentPagination, componentSearchTerm, componentActiveFilter } = state.bom;
    
    const params: SearchComponentsRequest = action.payload || {
      pageNum: componentPagination.currentPage,
      pageSize: componentPagination.pageSize,
      filter: {
        ...(componentSearchTerm && { Name: componentSearchTerm }),
        ...(componentActiveFilter !== null && { Active: componentActiveFilter }),
      },
    };
    
    const response: SearchComponentsResponse = yield call(bomApi.getAllComponents, params);
    yield put(fetchComponentsSuccess(response));
  } catch (error: any) {
    yield put(fetchComponentsFailure(error.message || 'Failed to fetch components'));
  }
}

function* fetchComponentByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const component: Component = yield call(bomApi.getComponentById, action.payload);
    yield put(fetchComponentByIdSuccess(component));
  } catch (error: any) {
    yield put(fetchComponentByIdFailure(error.message || 'Failed to fetch component'));
  }
}

function* createComponentApi(action: PayloadAction<CreateComponentRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = bomApi.validateComponent(action.payload);
    if (validationErrors.length > 0) {
      yield put(setComponentValidationErrors(validationErrors));
      yield put(createComponentFailure('Validation failed'));
      return;
    }
    
    const component: Component = yield call(bomApi.createComponent, action.payload);
    yield put(createComponentSuccess(component));
    
    // Refresh the list and dropdowns
    yield put(fetchComponentsStart());
    yield put(fetchDropdownsStart());
  } catch (error: any) {
    yield put(createComponentFailure(error.message || 'Failed to create component'));
  }
}

function* updateComponentApi(action: PayloadAction<Partial<Component>>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = bomApi.validateComponent(action.payload);
    if (validationErrors.length > 0) {
      yield put(setComponentValidationErrors(validationErrors));
      yield put(updateComponentFailure('Validation failed'));
      return;
    }
    
    const component: Component = yield call(bomApi.updateComponent, action.payload);
    yield put(updateComponentSuccess(component));
    
    // Refresh the list and dropdowns
    yield put(fetchComponentsStart());
    yield put(fetchDropdownsStart());
  } catch (error: any) {
    yield put(updateComponentFailure(error.message || 'Failed to update component'));
  }
}

function* deleteComponentApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(bomApi.deleteComponent, action.payload);
    yield put(deleteComponentSuccess(action.payload));
    
    // Refresh the list and dropdowns
    yield put(fetchComponentsStart());
    yield put(fetchDropdownsStart());
  } catch (error: any) {
    yield put(deleteComponentFailure(error.message || 'Failed to delete component'));
  }
}

// Product Sagas
function* fetchProductsApi(action: PayloadAction<SearchProductsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { productPagination, productSearchTerm, productActiveFilter } = state.bom;
    
    const params: SearchProductsRequest = action.payload || {
      pageNum: productPagination.currentPage,
      pageSize: productPagination.pageSize,
      filter: {
        ...(productSearchTerm && { Name: productSearchTerm }),
        ...(productActiveFilter !== null && { Active: productActiveFilter }),
      },
    };
    
    const response: SearchProductsResponse = yield call(bomApi.getAllProducts, params);
    yield put(fetchProductsSuccess(response));
  } catch (error: any) {
    yield put(fetchProductsFailure(error.message || 'Failed to fetch products'));
  }
}

function* fetchProductByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const product: Product = yield call(bomApi.getProductById, action.payload);
    yield put(fetchProductByIdSuccess(product));
  } catch (error: any) {
    yield put(fetchProductByIdFailure(error.message || 'Failed to fetch product'));
  }
}

function* createProductApi(action: PayloadAction<CreateProductRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = bomApi.validateProduct(action.payload);
    if (validationErrors.length > 0) {
      yield put(setProductValidationErrors(validationErrors));
      yield put(createProductFailure('Validation failed'));
      return;
    }
    
    const product: Product = yield call(bomApi.createProduct, action.payload);
    yield put(createProductSuccess(product));
    
    // Refresh the list and dropdowns
    yield put(fetchProductsStart());
    yield put(fetchDropdownsStart());
  } catch (error: any) {
    yield put(createProductFailure(error.message || 'Failed to create product'));
  }
}

function* updateProductApi(action: PayloadAction<Partial<Product>>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = bomApi.validateProduct(action.payload);
    if (validationErrors.length > 0) {
      yield put(setProductValidationErrors(validationErrors));
      yield put(updateProductFailure('Validation failed'));
      return;
    }
    
    const product: Product = yield call(bomApi.updateProduct, action.payload);
    yield put(updateProductSuccess(product));
    
    // Refresh the list and dropdowns
    yield put(fetchProductsStart());
    yield put(fetchDropdownsStart());
  } catch (error: any) {
    yield put(updateProductFailure(error.message || 'Failed to update product'));
  }
}

function* deleteProductApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(bomApi.deleteProduct, action.payload);
    yield put(deleteProductSuccess(action.payload));
    
    // Refresh the list and dropdowns
    yield put(fetchProductsStart());
    yield put(fetchDropdownsStart());
  } catch (error: any) {
    yield put(deleteProductFailure(error.message || 'Failed to delete product'));
  }
}

// Work Order Sagas
function* fetchWorkOrdersApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const workOrders: WorkOrder[] = yield call(bomApi.getWorkOrdersForProduct, action.payload);
    yield put(fetchWorkOrdersSuccess(workOrders));
  } catch (error: any) {
    yield put(fetchWorkOrdersFailure(error.message || 'Failed to fetch work orders'));
  }
}

function* linkProductToWorkOrderApi(action: PayloadAction<{productId: string, workOrderId: string}>): Generator<any, void, any> {
  try {
    yield call(bomApi.linkProductToWorkOrder, action.payload.productId, action.payload.workOrderId);
    
    // Fetch the work order details to add to the linked list
    const workOrders: WorkOrder[] = yield call(bomApi.getWorkOrdersForProduct, action.payload.productId);
    const linkedWorkOrder = workOrders.find(wo => wo.id === action.payload.workOrderId);
    
    if (linkedWorkOrder) {
      yield put(linkProductToWorkOrderSuccess(linkedWorkOrder));
    }
  } catch (error: any) {
    yield put(linkProductToWorkOrderFailure(error.message || 'Failed to link product to work order'));
  }
}

function* unlinkProductFromWorkOrderApi(action: PayloadAction<{productId: string, workOrderId: string}>): Generator<any, void, any> {
  try {
    yield call(bomApi.unlinkProductFromWorkOrder, action.payload.productId, action.payload.workOrderId);
    yield put(unlinkProductFromWorkOrderSuccess(action.payload.workOrderId));
  } catch (error: any) {
    yield put(unlinkProductFromWorkOrderFailure(error.message || 'Failed to unlink product from work order'));
  }
}

// BOM Structure Sagas
function* fetchBOMStructureApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const structure: BOMStructure = yield call(bomApi.getBOMStructure, action.payload);
    yield put(fetchBOMStructureSuccess(structure));
  } catch (error: any) {
    yield put(fetchBOMStructureFailure(error.message || 'Failed to fetch BOM structure'));
  }
}

// Dropdown Data Sagas
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    const [components, products, workOrders]: [Component[], Product[], WorkOrder[]] = yield all([
      call(bomApi.getActiveComponents),
      call(bomApi.getActiveProducts),
      call(bomApi.getAvailableWorkOrders),
    ]);
    
    yield put(fetchDropdownsSuccess({
      components,
      products,
      workOrders,
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Export Sagas
function* exportBOMApi(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { boms } = state.bom;
    
    const csvContent = bomApi.exportBOMToCSV(boms);
    const filename = `bom-export-${new Date().toISOString().split('T')[0]}.csv`;
    
    bomApi.downloadCSV(csvContent, filename);
    yield put(exportBOMSuccess());
  } catch (error: any) {
    yield put(exportBOMFailure(error.message || 'Failed to export BOMs'));
  }
}

// Watcher Sagas for automatic refreshing based on filters/pagination
function* watchBOMFilters(): Generator<any, void, any> {
  const state: RootState = yield select();
  const { bomSearchTerm, bomActiveFilter, bomPagination } = state.bom;
  
  // Trigger fetch when filters change
  yield put(fetchBOMsStart({
    pageNum: bomPagination.currentPage,
    pageSize: bomPagination.pageSize,
    filter: {
      ...(bomSearchTerm && { Name: bomSearchTerm }),
      ...(bomActiveFilter !== null && { Active: bomActiveFilter }),
    },
  }));
}

function* watchComponentFilters(): Generator<any, void, any> {
  const state: RootState = yield select();
  const { componentSearchTerm, componentActiveFilter, componentPagination } = state.bom;
  
  // Trigger fetch when filters change
  yield put(fetchComponentsStart({
    pageNum: componentPagination.currentPage,
    pageSize: componentPagination.pageSize,
    filter: {
      ...(componentSearchTerm && { Name: componentSearchTerm }),
      ...(componentActiveFilter !== null && { Active: componentActiveFilter }),
    },
  }));
}

function* watchProductFilters(): Generator<any, void, any> {
  const state: RootState = yield select();
  const { productSearchTerm, productActiveFilter, productPagination } = state.bom;
  
  // Trigger fetch when filters change
  yield put(fetchProductsStart({
    pageNum: productPagination.currentPage,
    pageSize: productPagination.pageSize,
    filter: {
      ...(productSearchTerm && { Name: productSearchTerm }),
      ...(productActiveFilter !== null && { Active: productActiveFilter }),
    },
  }));
}

// Root Saga
export default function* bomSaga(): Generator<any, void, any> {
  yield all([
    // BOM Watchers
    takeEvery(fetchBOMsStart.type, fetchBOMsApi),
    takeLatest(fetchBOMByIdStart.type, fetchBOMByIdApi),
    takeEvery(createBOMStart.type, createBOMApi),
    takeEvery(updateBOMStart.type, updateBOMApi),
    takeEvery(deleteBOMStart.type, deleteBOMApi),
    
    // Component Watchers
    takeEvery(fetchComponentsStart.type, fetchComponentsApi),
    takeLatest(fetchComponentByIdStart.type, fetchComponentByIdApi),
    takeEvery(createComponentStart.type, createComponentApi),
    takeEvery(updateComponentStart.type, updateComponentApi),
    takeEvery(deleteComponentStart.type, deleteComponentApi),
    
    // Product Watchers
    takeEvery(fetchProductsStart.type, fetchProductsApi),
    takeLatest(fetchProductByIdStart.type, fetchProductByIdApi),
    takeEvery(createProductStart.type, createProductApi),
    takeEvery(updateProductStart.type, updateProductApi),
    takeEvery(deleteProductStart.type, deleteProductApi),
    
    // Work Order Watchers
    takeEvery(fetchWorkOrdersStart.type, fetchWorkOrdersApi),
    takeEvery(linkProductToWorkOrderStart.type, linkProductToWorkOrderApi),
    takeEvery(unlinkProductFromWorkOrderStart.type, unlinkProductFromWorkOrderApi),
    
    // BOM Structure Watchers
    takeLatest(fetchBOMStructureStart.type, fetchBOMStructureApi),
    
    // Dropdown Watchers
    takeEvery(fetchDropdownsStart.type, fetchDropdownsApi),
    
    // Export Watchers
    takeEvery(exportBOMStart.type, exportBOMApi),
    
    // Filter Watchers (using takeLatest to debounce rapid filter changes)
    takeLatest([
      'bom/setBOMSearchTerm',
      'bom/setBOMActiveFilter',
      'bom/setBOMPage',
      'bom/setBOMPageSize'
    ], watchBOMFilters),
    
    takeLatest([
      'bom/setComponentSearchTerm',
      'bom/setComponentActiveFilter',
      'bom/setComponentPage',
      'bom/setComponentPageSize'
    ], watchComponentFilters),
    
    takeLatest([
      'bom/setProductSearchTerm',
      'bom/setProductActiveFilter',
      'bom/setProductPage',
      'bom/setProductPageSize'
    ], watchProductFilters),
  ]);
} 